import { ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import {
  CardAction,
  TCardActionProps,
} from '@/common/components/CardAction/CardAction';
import { IconBadgeFile } from '@/common/components/IconBadgeFile/IconBadgeFile';
import { Icon } from '@/common/components/Icons/Icon';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { pickImageFromCamera } from '@/common/utils/media-library';
import { pickImageFromLibrary } from '@/common/utils/media-library';
import { pickDocumentsFromLibrary } from '@/common/utils/media-library';
import { IFile } from '@coverflex-tech/hypernova-utils';
import { useActionSheet } from '@expo/react-native-action-sheet';

export type TCardActionUploadProps = TCardActionProps & {
  loading?: boolean;
  file?: IFile;
  acceptedFiles?: string;
  onChange: (file?: IFile) => void;
};

export function CardActionUpload({
  description,
  disabled,
  file,
  acceptedFiles,
  loading,
  onChange,
  plain,
  ...cardActionProps
}: TCardActionUploadProps) {
  const { t } = useTranslation();
  const { md } = useBreakpoints(['md']);
  const inputRef = useRef<HTMLInputElement>(null);
  const { showActionSheetWithOptions } = useActionSheet();

  const onUpload = async () => {
    const options = [
      t('general.action.open_camera'),
      t('general.action.open_gallery'),
      t('general.action.open_documents'),
      t('general.navigation.cancel'),
    ];
    if (Platform.OS !== 'web') {
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: 3,
        },
        async (selectedIndex?: number) => {
          switch (selectedIndex) {
            case 0:
              const cameraImage = await pickImageFromCamera();
              if (cameraImage) {
                onChange(cameraImage);
              }
              break;
            case 1:
              const libraryImage = await pickImageFromLibrary();
              if (libraryImage) {
                onChange(libraryImage);
              }
              break;
            case 2:
              const documents = await pickDocumentsFromLibrary(acceptedFiles);
              if (documents) {
                onChange(documents);
              }
              break;
            case 3:
              break;
          }
        }
      );
    } else {
      inputRef.current?.click();
    }
  };

  const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onChange({
        name: file.name,
        url: URL.createObjectURL(file),
        blob: file,
      });
    }
  };

  const onRemove = () => {
    if (Platform.OS === 'web' && inputRef.current) {
      inputRef.current.value = '';
    }
    onChange();
  };

  return (
    <CardAction
      {...cardActionProps}
      icon={<IconBadgeFile fileUrl={file?.url} />}
      plain={plain || disabled}
      onPress={!file ? onUpload : undefined}
      description={
        file ? (
          <BodyText size="sm" color="neutral20" bold numberOfLines={1}>
            {file?.name}
          </BodyText>
        ) : (
          description
        )
      }
      action={
        <>
          {file ? (
            <Button
              variant="tertiary"
              size="sm"
              onPress={onRemove}
              icon={<Icon icon="trash-can" />}
              loading={loading}
              disabled={disabled}
            >
              {md ? t('general.action.remove') : undefined}
            </Button>
          ) : (
            <Button
              variant="tertiary"
              size="sm"
              icon={<Icon icon="upload" />}
              onPress={Platform.OS !== 'web' ? onUpload : undefined}
              loading={loading}
              disabled={disabled}
            >
              {md ? t('general.action.upload') : undefined}
            </Button>
          )}
          {Platform.OS === 'web' && (
            <input
              type="file"
              ref={inputRef}
              accept={acceptedFiles}
              onChange={onFileInputChange}
              className="hidden"
            />
          )}
        </>
      }
    />
  );
}
